import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { createStandaloneToast } from '@chakra-ui/react'
import { get } from 'lodash'
import { camelizeKeys } from 'humps'
import { getLocalAuth } from '../providers'
import { AUTH_STORAGE_KEY, ArcadeAuth } from 'ui/types'
import { checkClientVersion, storeAndCompareFeatureHash } from '../utils'
import { ApiStatusCodes, ApiMessages } from '../types/ApiResponse'
import { TERMS_REQUIRED_EVENT_KEY } from '../constants/events'
/*
The API endpoint needs to be pulled from localstorage after login;
Next doesn't allow accessing window during prerender; so this file
creates the Axios object on the fly when a method is called.

Using a proxy this way is a hack! If you are building from scratch,
don't do this! But given we already use `arcadeApiClient` all over
the codebase, this is a sneaky trick to prevent Next from complaining
about the non-existence of window.localStorage serverside.
*/
var clientId: string = ''
var clientMemo: AxiosInstance | null = null

// Token refresh state — shared across all client instances so concurrent
// 401s coalesce into a single refresh request.
let refreshPromise: Promise<ArcadeAuth> | null = null

export function createArcadeApiClient() {
  const auth = JSON.parse(
    window?.localStorage.getItem(AUTH_STORAGE_KEY) || '{}',
  )
  const currentClientId = auth.email + auth.token + auth.endpoint
  if (currentClientId === clientId && clientMemo) return clientMemo
  var client = axios.create({
    baseURL: auth.endpoint || process.env.ARCADE_API_ENDPOINT,
  })
  client.defaults.headers.common['X-User-Token'] = auth.token
  client.defaults.headers.common['X-User-Email'] = auth.email
  client.defaults.headers.common['Accept'] = 'application/json'

  client.interceptors.response.use(checkClientVersion)
  client.interceptors.response.use(
    storeAndCompareFeatureHash,
    checkIfUserRequiresVerification,
  )
  client.interceptors.response.use(checkIfTermsRequired)
  client.interceptors.response.use(camelizeResponse, refreshTokenOrLogout)
  clientId = currentClientId
  clientMemo = client
  return client
}

const handler = {
  // An Axios instance can act like an object or a function, so
  // use both `get` and `apply` handlers
  get(_target: object, prop: string, _receiver: any) {
    return createArcadeApiClient()[prop]
  },
  apply(_target: object, _thisArg: any, argumentList: [any]) {
    return createArcadeApiClient()(...argumentList)
  },
}

export const arcadeApiClient = new Proxy(() => {}, handler) as AxiosInstance

export default arcadeApiClient

function camelizeResponse(response) {
  /*
    Calling camelizeKeys on a blob prematurely converts the data to a string. This causes the axios handler for file downloading to not function as expected.
  */
  if (response.data instanceof Blob) {
    return response
  }

  response.data = camelizeKeys(response.data)
  return response
}

async function refreshTokenOrLogout(error: AxiosError) {
  const statusCode = get(error, 'response.status')
  const message = get(error, 'response.data.message')

  // Locked accounts can't be refreshed — logout immediately.
  if (
    statusCode === ApiStatusCodes.Forbidden &&
    message === ApiMessages.Locked
  ) {
    logout()
    return Promise.reject(error)
  }

  // Only attempt refresh on 401 Unauthorized.
  if (statusCode !== ApiStatusCodes.Unauthorized) {
    return Promise.reject(error)
  }

  const localAuth = getLocalAuth()
  const auth = localAuth.get()

  // No refresh token stored — nothing to try, logout.
  if (!auth.refreshToken) {
    logout()
    return Promise.reject(error)
  }

  // Don't retry if this request was itself already a retry after refresh.
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retryAfterRefresh?: boolean
  }
  if (!originalRequest || originalRequest._retryAfterRefresh) {
    logout()
    return Promise.reject(error)
  }

  try {
    // Coalesce concurrent 401s into a single refresh call.
    if (!refreshPromise) {
      refreshPromise = performTokenRefresh(auth)
    }
    const updatedAuth = await refreshPromise

    // Retry the original request with the new token via the refreshed
    // client so the response goes through the full interceptor chain
    // (camelizeResponse, checkClientVersion, etc.).
    originalRequest._retryAfterRefresh = true
    originalRequest.headers['X-User-Token'] = updatedAuth.token
    return createArcadeApiClient().request(originalRequest)
  } catch {
    // The session was replaced while the refresh ran (a password change
    // stored a new session). The old token failing is expected: retry with
    // the new session and do not log out.
    const current = getLocalAuth().get()
    if (current.refreshToken && current.refreshToken !== auth.refreshToken) {
      originalRequest._retryAfterRefresh = true
      originalRequest.headers['X-User-Token'] = current.token
      return createArcadeApiClient().request(originalRequest)
    }
    // Refresh failed — token is truly expired or revoked.
    logout()
    return Promise.reject(error)
  } finally {
    refreshPromise = null
  }
}

export async function performTokenRefresh(auth: ArcadeAuth): Promise<ArcadeAuth> {
  const endpoint = auth.endpoint || process.env.ARCADE_API_ENDPOINT
  const response = await axios.post(
    `${endpoint}/users/sessions/refresh`,
    {
      refresh_token: auth.refreshToken,
      source: 'arcade-multi-platform',
    },
  )
  const data = camelizeKeys(response.data) as Record<string, any>

  const updatedAuth: ArcadeAuth = {
    ...auth,
    authenticationToken: data.authenticationToken,
    token: data.authenticationToken,
    refreshToken: data.refreshToken,
  }

  // Persist so the proxy picks up the new token on the next call and
  // cross-tab sync propagates the refresh to other open tabs. If the stored
  // session changed while the refresh ran (a password change stored a new
  // session), keep that session: these tokens come from the revoked one.
  const localAuth = getLocalAuth()
  const current = localAuth.get()
  if (current.refreshToken !== auth.refreshToken) return current
  localAuth.set(updatedAuth)

  // Invalidate the memoized client so the next call rebuilds with the
  // new token header.
  clientId = ''
  clientMemo = null

  return updatedAuth
}

/**
 * A password change revokes every refresh token of the user and returns a new
 * session in the sign-in shape. Store it, so this tab stays signed in. A
 * response without a new session changes nothing.
 */
export function applyPasswordChangeSession(data: unknown): void {
  const session = data as
    | { authenticationToken?: string; refreshToken?: string }
    | undefined
  if (!session?.authenticationToken || !session.refreshToken) return

  const localAuth = getLocalAuth()
  localAuth.set({
    ...localAuth.get(),
    authenticationToken: session.authenticationToken,
    token: session.authenticationToken,
    refreshToken: session.refreshToken,
  })

  // Rebuild the memoized client with the new token header.
  clientId = ''
  clientMemo = null
}

const checkIfUserRequiresVerification = error => {
  const statusCode = get(error, 'response.status')
  const message = get(error, 'response.data.message')

  if (
    statusCode === ApiStatusCodes.Forbidden &&
    message === ApiMessages.RequiresVerification
  ) {
    const { toast } = createStandaloneToast()
    toast({
      title: 'Verification Required',
      description: 'Please verify your email address to continue.',
      status: 'info',
    })
  }

  return Promise.reject(error)
}

const checkIfTermsRequired = response => {
  if (response.headers['x-requires-terms'] === 'true') {
    const event = new CustomEvent(TERMS_REQUIRED_EVENT_KEY)
    window.dispatchEvent(event)
  }

  return response
}

function logout() {
  getLocalAuth().clear()
  window.location.href = '/login'
}
